import React, { useCallback, useContext } from 'react'
import WallboxLoadingPrioAccordion from 'src/Libs/wallboxes/components/loading-prio-settings/WallboxLoadingPrioAccordion'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { LoadingPriorisationModel, LoadingPriorisationFormModel } from 'src/Libs/wallboxes/models'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { InstallationOnlineStatusContext } from 'src/Libs/installations/context/installation-online-status-context'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'
import { OriginPage } from '../../types'
import { useSnackbarMessageBasedOnSoftwareVersion } from 'src/Libs/home-power-station'
import SystemSettingsBlankView from 'src/Libs/system-settings/component/SystemSettingsBlankView'
import { useFarmContext } from 'src/Elements/Farm'

interface Props {
  loadPrioSettings: LoadingPriorisationModel | null
  updateLoadPrioSettings: (model: LoadingPriorisationFormModel) => Promise<boolean>
  hasWritingRights?: boolean
  origin: OriginPage
}

/**
 * This is a self-contained component to display and manage the loading
 * prioritization for a wallbox.
 *
 * The default display type is an Accordion
 *
 * @constructor
 */
const WallboxLoadingPrioSettings: React.FC<Props> = ({ loadPrioSettings, updateLoadPrioSettings, hasWritingRights = true, origin }) => {
  const { t } = useCustomTranslation()
  const { enqueueErrorStack } = useSnackbar()
  const accordionName = t('systemSettings.smartFunctions.smartCharge.loadingPrio.sectionTitle')
  const pageName = origin === OriginPage.wallbox ? t('framework.wallbox') : t('systemSettings.smartFunctions.smartCharge.pageTitle')
  const { handleDisplaySnackbarMessageBasedOnSoftwareVersion } = useSnackbarMessageBasedOnSoftwareVersion()

  const handleUpdate = useCallback(
    async (values: LoadingPriorisationFormModel) => {
      try {
        await updateLoadPrioSettings(values)
        handleDisplaySnackbarMessageBasedOnSoftwareVersion({ pageName, accordionName, isEmsSetting: true })
      } catch (error) {
        console.error(error)
        enqueueErrorStack(t('common.systemSettings.api.error.failed-form-submission', { pageName, accordionName }))
        throw error
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueErrorStack, t, updateLoadPrioSettings, handleDisplaySnackbarMessageBasedOnSoftwareVersion]
  )
  const { isServerOnline } = useContext(InstallationOnlineStatusContext)
  const { isFarm } = useFarmContext()
  const isServerOffline = isFarm && isServerOnline !== true

  if (loadPrioSettings) {
    return (
      <OptionalTooltipWrapper
        hasTooltip={isServerOffline}
        placement={'top'}
        titleKey={'systemSettings.smartFunctions.smartCharge.loadingPrio.master-offline'}
      >
        <WallboxLoadingPrioAccordion
          model={loadPrioSettings}
          title={t('systemSettings.smartFunctions.smartCharge.loadingPrio.sectionTitle')}
          onUpdate={handleUpdate}
          hasWritingAccess={hasWritingRights}
          disabled={isServerOffline}
        />
      </OptionalTooltipWrapper>
    )
  }

  const blankViewVariant = isServerOffline ? 'error' : 'no-data'

  return (
    <OptionalTooltipWrapper
      hasTooltip={isServerOffline}
      placement={'top'}
      titleKey={'systemSettings.smartFunctions.smartCharge.loadingPrio.master-offline'}
    >
      <AccordionItem
        disabled={isServerOffline}
        expanded={!isServerOffline}
        title={t('systemSettings.smartFunctions.smartCharge.loadingPrio.sectionTitle')}
      >
        <SystemSettingsBlankView variant={blankViewVariant} />
      </AccordionItem>
    </OptionalTooltipWrapper>
  )
}

export default WallboxLoadingPrioSettings
